import { Button, Row, Separator, useOverlay, Paragraph } from '@fs/zion-ui'
import React, { useCallback, useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { usePageView } from '@fs/zion-analytics'
import { usePageTitle } from '../../app/PageTitleContext'
import { getReservationData } from '../../../services/personService'
import { PermissionsContext } from '../../app/PermissionsContext'
import ReleaseReservationsDialog from '../../reservation/ReleaseReservationDialog'
import { PersonContext } from '../PersonContext'
import CreateReservationsDialog from './CreateReservationsDialog'
import ReservationsTable from '../../reservation/ReservationsTable'
import FilterOrdinances from '../ordinances/FilterOrdinances'
import useNotifications from '../../../hooks/useNotifications'
import { enumToReadableCamelCase } from '../../../util/StringUtil'

export default function PersonReservations() {
  usePageView({ page_detail: 'DMConsole:PersonReservations' })
  const { error } = useNotifications()
  const { t } = useTranslation()
  usePageTitle(t('pageTitle.person.reservations'))
  const releaseDialogOpener = useOverlay()
  const { currentPerson, fetchCurrentPerson } = useContext(PersonContext)
  const currentPersonId = currentPerson?.id
  const { isAuthorized } = useContext(PermissionsContext)
  const [reservationData, setReservationData] = useState()
  const [checkedRanIds, setCheckedRanIds] = useState([])
  const [ordinanceType, setOrdinanceType] = useState('all')
  const filteredReservations =
    ordinanceType === 'all'
      ? reservationData?.reservations
      : reservationData.reservations.filter((res) => res.ordinanceType === ordinanceType)

  const isChecked = (ranId) => {
    return checkedRanIds.indexOf(ranId) !== -1
  }

  const anyChecked = () => {
    return checkedRanIds && checkedRanIds.length
  }

  const fetchReservationData = useCallback(async () => {
    if (currentPersonId) {
      try {
        // fetch reservation data
        const reservationDataResponse = await getReservationData(currentPersonId)
        setReservationData(reservationDataResponse.data)
      } catch (err) {
        error(t('person.reservations.errorFetchingReservations', `Error retrieving reservation data`), err)
      }
    }
  }, [currentPersonId, error, t])

  useEffect(() => {
    fetchReservationData()
  }, [fetchReservationData])

  if (!reservationData) {
    return null
  }
  if (reservationData && reservationData.reservations && reservationData.reservations.length === 0) {
    return (
      <Paragraph id="No Reservations Found" size="sm">
        {t('person.reservations.noReservations', 'No reservations found.')}
        <Separator size="sm" />
        <CreateReservationsDialog />
      </Paragraph>
    )
  }

  return (
    <>
      <Row>
        <CreateReservationsDialog />
        <Button
          id="Release Selected"
          variant="contained"
          onClick={releaseDialogOpener.open}
          disabled={!isAuthorized('ReserveOrdinancePermission') || !anyChecked()}
          emphasis="high"
        >
          {t('action.releaseSelected', 'Release Selected')}
        </Button>
        <ReleaseReservationsDialog
          {...releaseDialogOpener}
          onRelease={fetchCurrentPerson}
          reservationData={reservationData}
          isChecked={isChecked}
          checkedRanIds={checkedRanIds}
        />
        <FilterOrdinances currentOrdinanceType={ordinanceType} setOrdinanceType={setOrdinanceType} isReservationList />
      </Row>
      {filteredReservations.length > 0 ? (
        <ReservationsTable
          reservations={filteredReservations}
          personData={reservationData.personData}
          userNames={reservationData.contactNames}
          setCheckedRanIds={setCheckedRanIds}
        />
      ) : (
        <Paragraph id="No Filtered Reservations" size="sm">
          {t('person.reservations.noFilteredReservations', 'No {ordinanceType} reservations found.', {
            ordinanceType: enumToReadableCamelCase(ordinanceType),
          })}
        </Paragraph>
      )}
    </>
  )
}
